import { EntityIdSchema } from "@repo/core/universal";
import { BigNumberSchema, ConditionSchema, firstOrDie, SerializableSchema, z } from "@repo/utils";
import { CountryCode3Schema } from "@repo/countries/z";
import { OrganizationIdSchema } from "../organization/z.js";

export const ErpEntityIdSchema = EntityIdSchema("erp");
export const EntitySourceIdSchema = EntityIdSchema("src");
export const SqlEntitySourceIdSchema = EntityIdSchema("sql");

export const CustomerSchema = z.object({
    id: ErpEntityIdSchema,
    name: z.string(),
    externalId: z.union([z.string(), z.number()]),
    domains: z.array(z.string()),
});

export const AddressTypeSchema = z.enum(["billing", "delivery"]);

export const AddressSchema_OLD = z.object({
    id: ErpEntityIdSchema,
    externalId: z.union([z.number(), z.string()]),
    addressLine1: z._default(z.nullish(z.string()), null),
    addressLine2: z._default(z.nullish(z.string()), null),
    addressLine3: z._default(z.nullish(z.string()), null),
    street: z.nullable(z.string()),
    zip: z._default(z.nullish(z.string()), null),
    city: z._default(z.nullish(z.string()), null),
    county: z._default(z.nullish(z.string()), null),
    country: z._default(z.nullish(CountryCode3Schema), null),
    customerId: ErpEntityIdSchema,
    type: AddressTypeSchema,
});

export const ProductSchema = z.object({
    id: ErpEntityIdSchema,
    externalId: z.union([z.string(), z.number()]),
    active: z.boolean(),
    code: z.union([z.string(), z.number()]),
    title: z.string(),
});

export const EntitySourceDeduplicateSchema = z.union([z.boolean(), z.literal("last-win"), z.literal("merge")]);

export const EntitySourceConfigSchema = z.intersection(
    z.object({
        deduplicate: z.optional(EntitySourceDeduplicateSchema),
        filter: z.optional(ConditionSchema),
    }),
    z.discriminatedUnion("type", [
        z.object({
            type: z.literal("S3_EXPORT"),
            bucket: z.string(),
            key: z.string(),
            format: z.object({
                type: z.literal("CSV"),
                removeDuplicateLines: z.optional(z.boolean()),
                delimiter: z.optional(z.string()),
            }),
        }),
        z.object({
            type: z.literal("LEXINGTON_POC"),
        }),
        z.object({
            type: z.literal("ATP_POC"),
        }),
        z.object({
            type: z.literal("MANUAL"),
        }),
    ]),
);

export const EntitySourceSchema = z.object({
    id: EntitySourceIdSchema,
    organizationId: OrganizationIdSchema,
    entity: z.string(),
    config: EntitySourceConfigSchema,
});

export const EntityFieldTypeConfigSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("string"),
        originalName: z.string(),
        pk: z.boolean(),
        fk: z.optional(z.string()),
        regex: z.optional(z.string()),
    }),
    z.object({
        type: z.literal("integer"),
        originalName: z.string(),
        pk: z.boolean(),
        fk: z.optional(z.string()),
    }),
    z.object({
        type: z.literal("decimal"),
        originalName: z.string(),
    }),
    z.object({
        type: z.literal("constant"),
        value: z.string(),
        pk: z.boolean(),
    }),
    z.object({
        type: z.literal("active"),
        originalName: z.string(),
        true: z.string(),
        false: z.string(),
    }),
    z.object({
        type: z.literal("domain"),
        originalName: z.string(),
    }),
    z.object({
        type: z.literal("country"),
        originalName: z.string(),
    }),
    z.object({
        type: z.literal("boolean"),
        originalName: z.string(),
    }),
]);

export const EntityFieldConfigSchema = z.intersection(
    z.object({
        optional: z.optional(z.boolean()),
        description: z.optional(z.string()),
        llmSearch: z.optional(z.boolean()),
        comparisonInstructions: z.optional(z.string()),
        priority: z.optional(z.number()),
    }),
    EntityFieldTypeConfigSchema,
);

export const EntityFieldSchema = z.object({
    entitySourceId: EntitySourceIdSchema,
    name: z.string(),
    config: EntityFieldConfigSchema,
});

export const SqlEntityFieldSchema = z.object({
    originalName: z.string(),
    defaultValue: z.optional(z.nullable(z.union([z.string(), z.number(), z.boolean()]))),
});

export const SuppliersEntitySourceSchema = z.object({
    entity: z.literal("suppliers"),
    fields: z.object({
        externalId: SqlEntityFieldSchema,
        name1: z.optional(SqlEntityFieldSchema),
        name2: z.optional(SqlEntityFieldSchema),
        additionalDeliveryDays: z.optional(SqlEntityFieldSchema),
        additionalShipmentDays: z.optional(SqlEntityFieldSchema),
    }),
    arbitrary: z.optional(z.record(z.string(), SqlEntityFieldSchema)),
});
export const DeliveryTermsEntitySourceSchema = z.object({
    entity: z.literal("delivery_terms"),
    fields: z.object({
        externalId: SqlEntityFieldSchema,
        description1: z.optional(SqlEntityFieldSchema),
        description2: z.optional(SqlEntityFieldSchema),
    }),
    arbitrary: z.optional(z.record(z.string(), SqlEntityFieldSchema)),
});

export const SqlEntitySourceTargetSchema = z.discriminatedUnion("entity", [
    SuppliersEntitySourceSchema,
    DeliveryTermsEntitySourceSchema,
]);

export const SqlEntitySourceSchema = z.object({
    id: SqlEntitySourceIdSchema,
    organizationId: OrganizationIdSchema,
    source: EntitySourceConfigSchema,
    target: SqlEntitySourceTargetSchema,
});

export const SupplierIdSchema = EntityIdSchema("sup");

export const SupplierSchema = z.object({
    id: SupplierIdSchema,
    organizationId: OrganizationIdSchema,
    externalId: z.nullable(z.string()),
    name1: z.nullable(z.string()),
    name2: z.nullable(z.string()),
    additionalDeliveryDays: z.nullable(z.number()),
    additionalShipmentDays: z.nullable(z.number()),
    arbitrary: z.nullable(z.record(z.string(), SerializableSchema)),
    settings: z.nullable(SerializableSchema),
});

export const DeliveryTermsIdSchema = EntityIdSchema("dtm");

export const DeliveryTermsSchema = z.object({
    id: DeliveryTermsIdSchema,
    organizationId: OrganizationIdSchema,
    externalId: z.nullable(z.string()),
    description1: z.nullable(z.string()),
    description2: z.nullable(z.string()),
    arbitrary: z.nullable(z.record(z.string(), SerializableSchema)),
});

export const PurchaseOrderHeadSchema = z.object({
    id: z.string(),
    number: z.string(),
    date: z.string(),
    currency: z.nullable(z.string()),
    supplierId: z.nullable(z.string()),
    paymentTermsId: z.nullable(z.string()),
    deliveryTermsId: z.nullable(z.string()),
    status: z.nullable(z.string()),
    responsibleBuyer1: z.nullable(z.string()),
    responsibleBuyer2: z.nullable(z.string()),
    responsibleBuyer3: z.nullable(z.string()),
    purchaserOrg: z.nullable(z.string()),
    purchaserGroup: z.nullable(z.string()),
    docCategory: z.nullable(z.string()),
    docType: z.nullable(z.string()),
    companyCode: z.nullable(z.string()),
});

export const PurchaseOrderPaymentTermsSchema = z.object({
    id: z.string(),
    text: z.string(),
});

export const PurchaseOrderDeliveryTermsSchema = z.object({
    id: z.string(),
    incoterms: z.string(),
    location: z.nullable(z.string()),
});

export const PurchaseOrderItemSchema = z.object({
    id: z.string(),
    status: z.nullable(z.string()),
    poId: z.string(),
    poNumber: z.string(),
    positionNumber: z.string(),
    ocReference: z.nullable(z.string()),
    materialId: z.nullable(z.string()),
    description1: z.nullable(z.string()),
    description2: z.nullable(z.string()),
    description3: z.nullable(z.string()),
    supplierSku: z.nullable(z.string()),
    deliveryAddress: z.nullable(z.string()),
    quantity: z.nullable(z.number()),
    quantityUom: z.nullable(z.string()),
    unitPrice: z.nullable(z.number()),
    priceUom: z.nullable(z.string()),
    priceUnit: z.nullable(z.number()),
    uomConversionCoefficient: z.number(),
    netTotalPrice: z.nullable(z.number()),
    grossTotalPrice: z.nullable(z.number()),
    contract: z.nullable(z.string()),
    requisitioner: z.nullable(z.string()),
    requisitionNumber: z._default(z.nullable(z.string()), null),
    requisitionItem: z._default(z.nullable(z.string()), null),
    dispo: z.nullable(z.string()),
    plant: z.nullable(z.string()),
    confirmationControlKey: z.nullable(z.string()),
});

export const PurchaseOrderScheduleSchema = z.object({
    id: z.string(),
    poItemId: z.string(),
    scheduleNumber: z.string(),
    positionNumber: z.string(),
    deliveryDate: z.string(),
    deliveryDate2: z.nullable(z.string()),
    quantity: z.number(),
    requisitionNumber: z._default(z.nullable(z.string()), null),
    requisitionItem: z._default(z.nullable(z.string()), null),
});

export const AddressSchema = z.object({
    id: z.string(),
    addressLine1: z.nullable(z.string()),
    addressLine2: z.nullable(z.string()),
    addressLine3: z.nullable(z.string()),
    addressLine4: z.nullable(z.string()),
    street: z.nullable(z.string()),
    houseNumber: z.nullable(z.string()),
    zip: z.nullable(z.string()),
    city: z.nullable(z.string()),
    county: z.nullable(z.string()),
    country: z.nullable(z.string()),
    code: z.nullable(z.string()),
    type: AddressTypeSchema,
});

export const PurchaseOrderConfirmationSchema = z.object({
    positionNumber: z.string(),
    serialNumber: z.number(),
    deliveryDate: z.nullable(z.string()),
    quantity: z.nullable(z.number()),
    reference: z.nullable(z.string()),
});

export const PurchaseOrderExtraTypeSchema = z.enum([
    "unknown",
    "discount",
    "packing",
    "freight",

    "metalSurcharge",
    "silverSurcharge",
    "goldSurcharge",
    "copperSurcharge",
    "brassSurcharge",
    "platinumSurcharge",
    "aluminumSurcharge",
    "leadSurcharge",
    "tungstenSurcharge",

    "alloySurcharge",
    "paintingSurcharge",
    "expressSurcharge",
    "lowValueSurcharge",
    "lowQuantitySurcharge",
    "setupCostSurcharge",
    "sampleCostSurcharge",
    "inflationSurcharge",
    "additionalPartsSurcharge",
    "modelCostSurcharge",

    "generalSurcharge",
]);

export const PurchaseOrderPercentageExtraAppliedToSchema = z.enum(["gross", "net"]);

export const PurchaseOrderExtraCalculationSchema = z.discriminatedUnion("class", [
    z.object({
        class: z.literal("percentage"),
        rate: z.nullable(BigNumberSchema),
        appliedTo: PurchaseOrderPercentageExtraAppliedToSchema,
    }),
    z.object({
        class: z.literal("fixedAmount"),
        amount: z.nullable(BigNumberSchema),
        distributed: z.boolean(),
    }),
    z.object({
        class: z.literal("quantityBased"),
        quantity: BigNumberSchema,
        quantityUom: z.string(),
        unitPrice: BigNumberSchema,
        priceUom: z.string(),
        perAmount: BigNumberSchema,
        uomConversionCoefficient: BigNumberSchema,
    }),
]);

export const PurchaseOrderExtraCalculationDefinitionSchema = z.discriminatedUnion("class", [
    z.object({
        class: z.literal("percentage"),
        appliedTo: PurchaseOrderPercentageExtraAppliedToSchema,
    }),
    z.object({
        class: z.literal("fixedAmount"),
        distributed: z.boolean(),
    }),
    z.object({
        class: z.literal("quantityBased"),
    }),
]);

export const PurchaseOrderExtraCalculationClassSchema = z.enum(
    PurchaseOrderExtraCalculationSchema.def.options.map(x => firstOrDie(x.def.shape.class.def.values)),
);

export const PurchaseOrderExtraPlacementSchema = z.enum(["item", "header", "both"]);

export const PurchaseOrderExtraDefinitionSchema = z.record(
    z.string(),
    z.object({
        type: PurchaseOrderExtraTypeSchema,
        placement: PurchaseOrderExtraPlacementSchema,
        calculation: PurchaseOrderExtraCalculationDefinitionSchema,
        default: z.optional(z.boolean()),
    }),
);

export const PurchaseOrderExtraExternalIdSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("Sap"),
        PricingDocument: z.string(),
        PricingDocumentItem: z.nullable(z.string()),
        PricingProcedureCounter: z.string(),
        PricingProcedureStep: z.string(),
        ConditionType: z.string(),
    }),
]);

export const PurchaseOrderExtraSchema = z.object({
    id: z.string(),
    externalId: PurchaseOrderExtraExternalIdSchema,
    calculation: PurchaseOrderExtraCalculationSchema,
});

export const PurchaseOrderExtrasSchema = z.object({
    header: z.array(PurchaseOrderExtraSchema),
    items: z.record(z.string(), z.array(PurchaseOrderExtraSchema)),
});

export const PurchaseOrderSchema = z.object({
    head: PurchaseOrderHeadSchema,
    deliveryTerms: z.record(z.string(), PurchaseOrderDeliveryTermsSchema),
    paymentTerms: z.record(z.string(), PurchaseOrderPaymentTermsSchema),
    items: z.array(PurchaseOrderItemSchema),
    confirmations: z.record(z.string(), z.array(PurchaseOrderConfirmationSchema)),
    schedules: z.record(z.string(), z.array(PurchaseOrderScheduleSchema)),
    deliveryAddresses: z.record(z.string(), AddressSchema),
    extras: PurchaseOrderExtrasSchema,
});

export const PurchaseOrderArbitrarySchema = z.record(z.string(), SerializableSchema);

export const PurchaseOrderConfirmationUpdateSchema = z.object({
    quantity: BigNumberSchema,
    deliveryDate: z.string(),
    reference: z.nullable(z.string()),
});

export const PurchaseOrderExtraLineUpdateSchema = z.object({
    type: PurchaseOrderExtraTypeSchema,
    calculation: PurchaseOrderExtraCalculationSchema,
});

export const PurchaseOrderItemUpdateSchema = z.object({
    id: z.string(),
    serialNumber: z.optional(z.number()),
    supplierSku: z.nullable(z.string()),
    unitPrice: z.nullable(BigNumberSchema),
    quantity: z.nullable(BigNumberSchema),
    extra: z.nullable(z.array(PurchaseOrderExtraLineUpdateSchema)),
    confirmationNumber: z.optional(z.nullable(z.string())),
    confirmations: z.nullable(z.array(PurchaseOrderConfirmationUpdateSchema)),
});

export const PurchaseOrderMasterPriceUpdateSchema = z.object({
    positions: z.array(
        z.object({
            id: z.string(),
            value: z.nullable(BigNumberSchema),
            masterPriceId: z.optional(z.string()),
        }),
    ),
});

export const PurchaseOrderUpdateSchema = z.object({
    number: z.string(),
    head: z.object({
        incoterms1: z.nullable(z.string()),
        incoterms2: z.nullable(z.string()),
        paymentTerms: z.nullable(z.string()),
    }),
    items: z.array(PurchaseOrderItemUpdateSchema),
    headerExtra: z.nullable(z.array(PurchaseOrderExtraLineUpdateSchema)),
    masterPrice: z.optional(PurchaseOrderMasterPriceUpdateSchema),
    arbitrary: z.optional(z.record(z.string(), SerializableSchema)),
});

export const PostgresPurchaseOrderSchema = PurchaseOrderSchema;
export const PostgresPurchaseOrderUpdateSchema = z.object({
    head: z.object({
        deliveryTermId: z.optional(z.string()),
        paymentTermsId: z.optional(z.string()),
    }),
    items: z.record(
        z.string(),
        z.object({
            supplierSku: z.optional(z.string()),
            unitPrice: z.optional(z.number()),
            quantity: z.optional(z.number()),
        }),
    ),
    confirmations: z.record(z.string(), z.array(PurchaseOrderConfirmationSchema)),
    schedules: z.record(z.string(), z.array(PurchaseOrderScheduleSchema)),
    deliveryTerms: z.record(z.string(), PurchaseOrderDeliveryTermsSchema),
    paymentTerms: z.record(z.string(), PurchaseOrderPaymentTermsSchema),
});

export const SapScheduleLineDeliveryDateFormatSchema = z.enum(["dd.MM.yyyy", "yyyy-MM-dd"]);

export const IntegrationConnectionSchema = z.object({
    baseUrl: z.string(),
    query: z.optional(z.record(z.string(), z.string())),
    headers: z.optional(z.record(z.string(), z.string())),
});

export const IntegrationAuthSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("BASIC"),
        username: z.string(),
        password: z.string(),
    }),
    z.object({
        type: z.literal("OAUTH2_CLIENT_CREDENTIALS"),
        tokenUrl: z.string(),
        clientId: z.string(),
        clientSecret: z.string(),
        scope: z.string(),
    }),
]);

export const XMLNode = z.object({
    tag: z.string(),
    attrs: z.optional(z.record(z.string(), z.string())),
});

export const XMLPathSchema = z.array(XMLNode);

export const PushAttachmentsViaFormDataSchema = z.object({
    type: z.literal("formData"),
    url: z.string(),
    query: z.optional(z.record(z.string(), z.string())),
    params: z.record(z.string(), z.string()),
});

/**
 * @deprecated Use PushAttachmentsViaXml - it's more general and cover BAPI
 */
export const PushAttachmentsViaBapiSchema = z.object({
    type: z.literal("BAPI"),
    url: z.string(),
    query: z.optional(z.record(z.string(), z.string())),
    requestBodyPath: XMLPathSchema,
    numberNode: z.string(),
    contentNode: z.string(),
});

export const PushAttachmentsViaXmlSchema = z.object({
    type: z.literal("xml"),
    connection: IntegrationConnectionSchema,
    auth: z.optional(IntegrationAuthSchema),
    requestBodyPath: XMLPathSchema,
    bodyDefinition: z.record(z.string(), z.string()),
});

export const PushAttachmentsViaBinaryBodySchema = z.object({
    type: z.literal("binary"),
    method: z.string(),
    url: z.string(),
    headers: z.optional(z.record(z.string(), z.string())),
});

export const SharePointAuthSchema = z.object({
    tenantId: z.string(),
    clientId: z.string(),
    clientSecret: z.string(),
});

export const PushAttachmentsToSharePointSchema = z.object({
    type: z.literal("sharepoint"),
    auth: SharePointAuthSchema,
    driveId: z.string(),
    path: z.string(),
    columns: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
});

export const PushAttachmentsConfigSchema = z.intersection(
    z.object({
        format: z.optional(z.enum(["pdf", "eml"])),
    }),
    z.discriminatedUnion("type", [
        PushAttachmentsViaFormDataSchema,
        PushAttachmentsViaBapiSchema,
        PushAttachmentsViaBinaryBodySchema,
        PushAttachmentsToSharePointSchema,
        PushAttachmentsViaXmlSchema,
    ]),
);

export const SapBapiTagFormatSchema = z.enum(["screaming_snake", "pascal"]);

export const SapBapiPoGetDetail1Schema = z.object({
    type: z.literal("BAPI_PO_GETDETAIL1"),
    requestBodyPath: XMLPathSchema,
    responsePath: z.array(z.string()),
    override: z.optional(IntegrationConnectionSchema),
    format: z._default(SapBapiTagFormatSchema, "screaming_snake"),
});

export const SapBapiPoChangeSchema = z.object({
    type: z.literal("BAPI_PO_CHANGE"),
    requestBodyPath: XMLPathSchema,
    responsePath: z.array(z.string()),
    override: z.optional(IntegrationConnectionSchema),
    format: z._default(SapBapiTagFormatSchema, "screaming_snake"),
});

export const SapBapiPoConfirm1Schema = z.object({
    type: z.literal("BAPI_PO_CONFIRM1"),
    requestBodyPath: XMLPathSchema,
    responsePath: z.array(z.string()),
    format: z._default(SapBapiTagFormatSchema, "screaming_snake"),
    numberNode: z.enum(["DOCUMENT_NO", "PURCHASEORDER"]),
    override: z.optional(IntegrationConnectionSchema),
});

export const SapBapiPrChangeSchema = z.object({
    type: z.literal("BAPI_PR_CHANGE"),
    requestBodyPath: XMLPathSchema,
    responsePath: z.array(z.string()),
    override: z.optional(
        z.object({
            baseUrl: z.string(),
            query: z.optional(z.record(z.string(), z.string())),
        }),
    ),
});

export const SapODataV4Schema = z.object({
    type: z.literal("ODataV4"),
    path: z.string(),
    query: z.optional(z.record(z.string(), z.string())),
    sessionIdCookie: z.string(),
});

export const SapIntegrationEndpointSchema = z.object({
    connection: IntegrationConnectionSchema,
    auth: z.optional(IntegrationAuthSchema),
    responseType: z.enum(["XML"]),
});

export const SapIntegrationSchema = z.object({
    type: z.literal("SAP"),
    connection: IntegrationConnectionSchema,
    auth: IntegrationAuthSchema,
    getPurchaseOrder: z.discriminatedUnion("type", [
        SapBapiPoGetDetail1Schema,
        SapBapiPoChangeSchema,
        SapODataV4Schema,
    ]),
    changePurchaseOrder: z.optional(z.discriminatedUnion("type", [SapBapiPoChangeSchema, SapODataV4Schema])),
    confirmPurchaseOrder: z.optional(SapBapiPoConfirm1Schema),
    changePurchaseRequisition: z.optional(SapBapiPrChangeSchema),
    pushAttachments: z.optional(PushAttachmentsConfigSchema),
    scheduleLineDeliveryDateFormat: z._default(SapScheduleLineDeliveryDateFormatSchema, "yyyy-MM-dd"),
    extras: z.optional(
        z.object({
            definition: z.optional(PurchaseOrderExtraDefinitionSchema),
        }),
    ),
    masterPrices: z.optional(
        z.discriminatedUnion("type", [
            z.object({
                type: z.literal("pricingConditionService"),
                name: z.string(),
                conditionTypes: z.array(z.string()),
                sessionCookieName: z.string(),
            }),
        ]),
    ),
    odataServices: z.optional(
        z.record(
            z.string(),
            z.object({
                connection: IntegrationConnectionSchema,
                auth: z.optional(IntegrationAuthSchema),
            }),
        ),
    ),
    endpoints: z.optional(z.record(z.string(), SapIntegrationEndpointSchema)),
});

export const PostgresErpIntegrationSchema = z.object({
    type: z.literal("POSTGRES_ERP"),
});

export const MsDynamicsReleaseApi = z.object({
    url: z.string(),
});

export const MsDynamicsIntegrationSchema = z.object({
    type: z.literal("MSDYNAMICS_ODATA_V4"),
    connection: z.object({
        url: z.string(),
        query: z.optional(z.record(z.string(), z.string())),
    }),
    auth: IntegrationAuthSchema,
    defaultCurrency: z._default(z.string(), "EUR"),
    releaseApi: z.optional(MsDynamicsReleaseApi),
    pushAttachments: z.optional(PushAttachmentsConfigSchema),
});

export const AswoIntegrationSchema = z.object({
    type: z.literal("ASWO"),
    connection: IntegrationConnectionSchema,
    auth: IntegrationAuthSchema,
});

export const ERPIntegrationSchema = z.discriminatedUnion("type", [
    SapIntegrationSchema,
    PostgresErpIntegrationSchema,
    MsDynamicsIntegrationSchema,
    AswoIntegrationSchema,
]);

export const PaymentTermsSchema = z.object({
    paymentTermsId: z.string(),
    textDe: z.optional(z.string()),
    textEn: z.optional(z.string()),
    textFr: z.optional(z.string()),
    textIt: z.optional(z.string()),
    textDa: z.optional(z.string()),
    textPl: z.optional(z.string()),
    textNl: z.optional(z.string()),
    textNo: z.optional(z.string()),
    textPt: z.optional(z.string()),
    textEs: z.optional(z.string()),
});

export const IncotermsSchema = z.object({
    incoterms: z.string(),
    text: z.optional(z.string()),
});

export const SyncedPurchaseOrder = z.object({
    number: z.string(),
    supplierEmail: z.string(),
});

export const SupplierSchema2 = z.object({
    id: z.string(),
    preventProcessing: z.optional(z.boolean()),
});

export const MaterialUnitSchema = z.object({
    materialId: z.string(),
    unit: z.string(),
    numerator: z.number(),
    denominator: z.number(),
});

export const AbstractEntitySourceIdSchema = EntityIdSchema("abs");

export const AbstractEntitySourceSourceSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("S3_EXPORT"),
        bucket: z.string(),
        key: z.string(),
        format: z.object({
            type: z.literal("CSV"),
            removeDuplicateLines: z.optional(z.boolean()),
            delimiter: z.optional(z.string()),
        }),
    }),
    z.object({
        type: z.literal("ODATA"),
        name: z.string(),
        path: z.optional(z.string()),
        query: z.optional(z.record(z.string(), z.string())),
        map: z.union([z.string(), z.array(z.string())]),
        syncState: z.optional(z.union([z.string(), z.array(z.string())])),
    }),
    z.object({
        type: z.literal("MANUAL"),
    }),
]);

export const AbstractEntitySourceFieldsSchema = z.record(
    z.string(),
    z.discriminatedUnion("type", [
        z.object({
            type: z.literal("string"),
            behavior: z.enum(["identity", "reference", "required", "optional"]),
            indexed: z.optional(z.boolean()),
        }),
    ]),
);

export const AbstractEntitySourceDefinitionSchema = z.object({
    source: AbstractEntitySourceSourceSchema,
    fields: AbstractEntitySourceFieldsSchema,
});

export const AbstractEntitySourceSchema = z.object({
    organizationId: OrganizationIdSchema,
    id: AbstractEntitySourceIdSchema,
    entity: z.string(),
    definition: AbstractEntitySourceDefinitionSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});

export const AbstractEntityIdSchema = EntityIdSchema("abe");

export const AbstractEntitySchema = z.object({
    organizationId: OrganizationIdSchema,
    id: AbstractEntityIdSchema,
    sourceId: AbstractEntitySourceIdSchema,
    identityKey: z.string(),
    data: SerializableSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});

export const AbstractEntityReferenceIdSchema = EntityIdSchema("abr");

export const AbstractEntityReferenceSchema = z.object({
    organizationId: OrganizationIdSchema,
    entityId: AbstractEntityIdSchema,
    type: z.string(),
    value: z.string(),
    createdAt: z.string(),
});

export const RequestBodySchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("JSON"),
        json: SerializableSchema,
    }),
    z.object({
        type: z.literal("TEXT"),
        text: z.string(),
    }),
]);

export const RequestParamsSchema = z.discriminatedUnion("method", [
    z.object({
        method: z.literal("GET"),
        path: z.optional(z.string()),
        headers: z.optional(z.record(z.string(), z.string())),
        cookies: z.optional(z.record(z.string(), z.string())),
        query: z.optional(z.record(z.string(), z.string())),
    }),
    z.object({
        method: z.literal("POST"),
        path: z.optional(z.string()),
        headers: z.optional(z.record(z.string(), z.string())),
        cookies: z.optional(z.record(z.string(), z.string())),
        query: z.optional(z.record(z.string(), z.string())),
        body: RequestBodySchema,
    }),
]);

export const MasterDataQuerySchema = z
    .discriminatedUnion("type", [
        z.object({
            type: z.literal("ENTITY_SOURCE"),
            entity: z.string(),
            idValues: z.array(z.string()),
            return: z.optional(z.string()),
        }),
        z.object({
            type: z.literal("ABSTRACT_ENTITY"),
            id: AbstractEntitySourceIdSchema,
            identity: z.record(z.string(), z.string()),
            return: z.optional(z.string()),
        }),
        z.object({
            type: z.literal("ODATA"),
            name: z.string(),
            path: z.optional(z.string()),
            query: z.optional(z.record(z.string(), z.string())),
            return: z.optional(z.union([z.string(), z.array(z.string())])),
        }),
        z.object({
            type: z.literal("ENDPOINT"),
            name: z.string(),
            params: RequestParamsSchema,
            return: z.optional(z.union([z.string(), z.array(z.string())])),
        }),
    ])
    .register(z.globalRegistry, { title: "MasterDataQuery" });
